import { CommonModule } from '@angular/common';
import { Component, inject, Injectable, Input, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { I18NService } from '@core';
import { ALAIN_I18N_TOKEN, I18nPipe } from '@delon/theme';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalService, NzModalModule } from 'ng-zorro-antd/modal';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { TestIdDirective } from 'src/app/shared/directives/test-id.directive';

import { HttpService } from '../../../../../http/http.service';
import { ConstantUtil } from '../../../../../util/constant.util';

export interface ChannelErrorReportData {
  channelType: string;
  description: string;
  errorMessage?: string;
  serviceNumberId?: string;
}

@Component({
  selector: 'channel-error-report',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NzModalModule,
    NzButtonModule,
    NzSelectModule,
    NzInputModule,
    NzFormModule,
    NzIconModule,
    I18nPipe,
    TestIdDirective
  ],
  template: `
    <!-- 錯誤報告模態框 -->
    <nz-modal
      [testId]="'channel-error-report-modal'"
      [(nzVisible)]="isErrorReportVisible"
      [nzTitle]="'tenant.channel.error-report.title' | i18n"
      nzWidth="572px"
      [nzMaskClosable]="false"
      nzWrapClassName="error-report-modal"
      (nzOnCancel)="handleCancel()"
      (nzOnOk)="handleSubmit()"
    >
      <ng-container *nzModalContent [testId]="'channel-error-report-modal-content'">
        <div class="form-group" [testId]="'channel-error-report-channel-group'">
          <label class="form-label" [testId]="'channel-error-report-channel-label'">{{
            'tenant.channel.error-report.channel-label' | i18n
          }}</label>
          <nz-select
            [testId]="'channel-error-report-channel-select'"
            [(ngModel)]="selectedChannel"
            [nzPlaceHolder]="'tenant.channel.error-report.channel-placeholder' | i18n"
            class="form-select"
          >
            <nz-option nzValue="LINE_OA" nzLabel="LINE OA" [testId]="'channel-error-report-option-line-oa'" />
            <nz-option nzValue="LINE_LOGIN" nzLabel="LINE Login" [testId]="'channel-error-report-option-line-login'" />
            <nz-option nzValue="Facebook" nzLabel="Facebook" [testId]="'channel-error-report-option-facebook'" />
            <nz-option nzValue="Instagram" nzLabel="Instagram" [testId]="'channel-error-report-option-instagram'" />
          </nz-select>
        </div>

        <div class="form-group" [testId]="'channel-error-report-description-group'">
          <label class="form-label" [testId]="'channel-error-report-description-label'">{{
            'tenant.channel.error-report.description-label' | i18n
          }}</label>
          <textarea
            [testId]="'channel-error-report-description-textarea'"
            nz-input
            [(ngModel)]="description"
            [placeholder]="'tenant.channel.error-report.description-placeholder' | i18n"
            class="form-textarea"
            [maxlength]="100"
          >
          </textarea>
          <div class="char-count" [class.warning]="description.length >= 100"> {{ description.length }} / 100 </div>
        </div>
      </ng-container>

      <div *nzModalFooter [testId]="'channel-error-report-modal-footer'">
        <button nz-button nzType="default" (click)="handleCancel()" [testId]="'channel-error-report-cancel-button'">{{
          'tenant.channel.error-report.cancel' | i18n
        }}</button>
        <button nz-button nzType="primary" (click)="handleSubmit()" [testId]="'channel-error-report-submit-button'">{{
          'tenant.channel.error-report.submit' | i18n
        }}</button>
      </div>
    </nz-modal>
  `,
  styles: [
    `
      ::ng-deep .ant-modal-confirm .anticon-close-circle {
        color: #ff4d4f !important;
      }

      ::ng-deep .error-report-modal .ant-modal-body {
        padding: 24px;
      }

      .form-group {
        margin-bottom: 16px;
      }

      .form-label {
        display: block;
        margin-bottom: 8px;
        color: #262626;
      }

      ::ng-deep .form-select {
        width: 100%;
      }

      ::ng-deep .form-select .ant-select-selector {
        padding: 8px 12px;
        border: 1px solid #d9d9d9;
        border-radius: 6px;
        font-size: 14px;
        transition: border-color 0.3s;
      }

      ::ng-deep .form-select:hover .ant-select-selector,
      ::ng-deep .form-select.ant-select-focused .ant-select-selector {
        border-color: #1890ff;
        box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
      }

      .form-textarea {
        width: 100%;
        min-height: 100px;
        padding: 8px 12px;
        border: 1px solid #d9d9d9;
        border-radius: 6px;
        font-size: 14px;
        resize: vertical;
        transition: border-color 0.3s;
      }

      .form-textarea:focus {
        border-color: #1890ff;
        outline: none;
        box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
      }

      .char-count {
        text-align: right;
        margin-top: 4px;
        color: #999;
        font-size: 12px;
      }

      .char-count.warning {
        color: #ff4d4f;
      }
    `
  ]
})
export class ChannelErrorReportComponent {
  isErrorReportVisible = false;

  selectedChannel = '';
  description = '';
  private readonly i18n = inject<I18NService>(ALAIN_I18N_TOKEN);
  constructor(
    private readonly modal: NzModalService,
    private readonly message: NzMessageService,
    private readonly httpService: HttpService
  ) {}
  /**
   * 顯示連接錯誤的 modal
   */
  showConnectionErrorModal(): void {
    this.modal.confirm({
      nzTitle: this.i18n.fanyi('tenant.channel.error-report.connection-failed'),
      nzContent: `
        <div style="margin-bottom: 16px;">
          ${this.i18n.fanyi('tenant.channel.error-report.connection-failed-content')}
        </div>
      `,
      nzOkText: this.i18n.fanyi('tenant.channel.error-report.report-error'),
      nzCancelText: this.i18n.fanyi('tenant.channel.error-report.cancel'),
      nzIconType: 'close-circle',
      nzWidth: '416px',
      nzOkType: 'primary',
      nzOkDanger: false,
      nzOnOk: () => {
        this.showErrorReportModal();
      },
      nzOnCancel: () => {
        // 取消操作
      }
    });
  }

  /**
   * 顯示錯誤報告表單 modal
   */
  showErrorReportModal(): void {
    this.isErrorReportVisible = true;
    this.selectedChannel = '';
    this.description = '';
  }

  /**
   * 處理取消操作
   */
  handleCancel(): void {
    this.isErrorReportVisible = false;
    this.selectedChannel = '';
    this.description = '';
  }

  /**
   * 處理提交操作
   */
  handleSubmit(): void {
    if (!this.selectedChannel) {
      this.message.warning(this.i18n.fanyi('tenant.channel.error-report.select-channel-required'));
      return;
    }

    if (!this.description.trim()) {
      this.message.warning(this.i18n.fanyi('tenant.channel.error-report.description-required'));
      return;
    }

    this.handleErrorRequest({
      channelType: this.selectedChannel,
      description: this.description.trim()
    });

    this.isErrorReportVisible = false;
  }
  /**
   * 處理錯誤報告請求
   */
  private handleErrorRequest(data: ChannelErrorReportData): void {
    const reportData = {
      type: 'Channel',
      name: this.i18n.fanyi('channel.connection-error', data.channelType),
      content: data.description
    };

    this.httpService.postFormData(ConstantUtil.BASE_REPAIR, reportData, null).subscribe({
      next: (res: any) => {
        if (res.success) {
          this.message.success(this.i18n.fanyi('tenant.channel.error-report.submit-success'));
        } else {
          this.message.error(this.i18n.fanyi('tenant.channel.error-report.submit-failed'));
        }
      },
      error: error => {
        console.error('提交錯誤報告失敗:', error);
        this.message.error(this.i18n.fanyi('tenant.channel.error-report.submit-failed'));
      }
    });
  }
}
